import { useEffect, useMemo, useRef, useState } from 'react';
import { Alert, Button } from 'react-bootstrap';
import Modal from '../../components/ModalVoltar';
import { api } from '../../lib/api';
import { aoErroImagem, urlOtimizada } from '../../lib/imagem';
import { useToast } from '../../context/ToastContext';
import { useConfirm } from '../../context/ConfirmContext';

// Aba "Fotos e Vídeo" — fotos do imóvel.
//
// Legado: escolher fotos → "SALVAR IMAGENS" (enviava junto com o formulário),
// remover uma (lixeira), "Remover Imagens" (todas) e uma tela separada
// "Ordenar Imagens" (arrastar). Aqui:
//   - fotos NOVAS ficam numa fila com pré-visualização e sobem quando o
//     imóvel é salvo (mesmo momento do legado) — `pendentes`/`setPendentes`
//     vêm da página;
//   - fotos já salvas: remover, remover todas e reordenar (arrastar, ou
//     ◀ ▶ no celular) direto aqui, sem tela separada. A primeira é a capa.
export const MAX_POR_VEZ = 50;

export default function GaleriaImovel({ publicId, fotos, aoAtualizarFotos, pendentes, setPendentes, podeEditar }) {
  const toast = useToast();
  const confirmar = useConfirm();
  const inputRef = useRef(null);
  const [confirmarTodas, setConfirmarTodas] = useState(false);
  const [arrastando, setArrastando] = useState(null);
  const [ampliada, setAmpliada] = useState(null); // índice da foto aberta no visualizador

  // pré-visualizações (URLs temporárias, liberadas quando a fila muda)
  const previews = useMemo(() => pendentes.map((f) => URL.createObjectURL(f)), [pendentes]);
  useEffect(() => () => previews.forEach((u) => URL.revokeObjectURL(u)), [previews]);

  function escolher(e) {
    const escolhidos = Array.from(e.target.files || []);
    e.target.value = '';
    const disponivel = MAX_POR_VEZ - pendentes.length;
    if (escolhidos.length > disponivel) {
      toast.erro(`Envie no máximo ${MAX_POR_VEZ} imagens por vez.`);
    }
    setPendentes([...pendentes, ...escolhidos.slice(0, Math.max(0, disponivel))]);
  }

  async function remover(foto) {
    if (!(await confirmar('Remover esta foto?'))) return;
    try {
      await api.delete(`/imoveis/${publicId}/imagens/${foto.id}`);
      aoAtualizarFotos(fotos.filter((f) => f.id !== foto.id));
      toast.sucesso('Imagem removida.');
    } catch (err) {
      toast.erro(err.message || 'Não foi possível remover a imagem');
    }
  }

  async function removerTodas() {
    setConfirmarTodas(false);
    try {
      await api.delete(`/imoveis/${publicId}/imagens`);
      aoAtualizarFotos([]);
      toast.sucesso('Imagens removidas.');
    } catch (err) {
      toast.erro(err.message || 'Não foi possível remover as imagens');
    }
  }

  async function reordenar(novaLista) {
    const anterior = fotos;
    aoAtualizarFotos(novaLista);
    try {
      await api.put(`/imoveis/${publicId}/imagens/ordem`, { ids: novaLista.map((f) => f.id) });
    } catch (err) {
      aoAtualizarFotos(anterior);
      toast.erro(err.message || 'Não foi possível reordenar as imagens');
    }
  }

  function mover(indice, delta) {
    const destino = indice + delta;
    if (destino < 0 || destino >= fotos.length) return;
    const lista = [...fotos];
    [lista[indice], lista[destino]] = [lista[destino], lista[indice]];
    reordenar(lista);
  }

  function soltar(indiceDestino) {
    if (arrastando === null || arrastando === indiceDestino) return setArrastando(null);
    const lista = [...fotos];
    const [movida] = lista.splice(arrastando, 1);
    lista.splice(indiceDestino, 0, movida);
    setArrastando(null);
    reordenar(lista);
  }

  const total = fotos.length + pendentes.length;

  return (
    <div>
      <Alert variant="light" className="border small">
        <ul className="mb-0">
          <li>Insira pelo menos 2 imagens para que este imóvel apareça nos portais.</li>
          <li>Envie no máximo {MAX_POR_VEZ} imagens por vez. São aceitos JPG, PNG e WEBP (o tamanho máximo de cada foto é definido em Configurações).</li>
          <li>A primeira foto é a capa. Arraste (ou use ◀ ▶) para reordenar.</li>
        </ul>
      </Alert>

      {podeEditar && (
        <div className="d-flex flex-wrap gap-2 mb-3">
          <input ref={inputRef} type="file" accept=".jpg,.jpeg,.png,.webp,image/jpeg,image/png,image/webp" multiple hidden onChange={escolher} />
          <Button variant="primary" onClick={() => inputRef.current.click()}>
            Selecionar imagens
          </Button>
          {publicId && fotos.length > 0 && (
            <Button variant="outline-danger" onClick={() => setConfirmarTodas(true)}>
              Remover todas as imagens
            </Button>
          )}
        </div>
      )}

      {pendentes.length > 0 && (
        <>
          <h6>Novas imagens ({pendentes.length}) — serão enviadas ao salvar</h6>
          <div className="d-flex flex-wrap gap-2 mb-3">
            {pendentes.map((arquivo, i) => (
              <div key={`${arquivo.name}-${i}`} style={{ width: 130 }} className="position-relative">
                <img src={previews[i]} alt={arquivo.name} className="img-fluid rounded border" style={{ height: 100, width: '100%', objectFit: 'cover' }} />
                <Button
                  size="sm"
                  variant="danger"
                  className="position-absolute top-0 end-0 m-1 py-0 px-1"
                  aria-label="Descartar"
                  onClick={() => setPendentes(pendentes.filter((_, j) => j !== i))}
                >
                  ×
                </Button>
              </div>
            ))}
          </div>
        </>
      )}

      {publicId && (
        <>
          <h6>Fotos do imóvel ({fotos.length})</h6>
          {fotos.length === 0 && <p className="text-muted">Nenhuma foto enviada ainda.</p>}
          <div className="d-flex flex-wrap gap-3">
            {fotos.map((foto, i) => (
              <div
                key={foto.id}
                style={{ width: 140, opacity: arrastando === i ? 0.4 : 1, cursor: podeEditar ? 'grab' : 'default' }}
                draggable={podeEditar}
                onDragStart={() => setArrastando(i)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => soltar(i)}
                onDragEnd={() => setArrastando(null)}
                className="border rounded p-1 bg-white"
              >
                <img
                  src={urlOtimizada(foto.url, 240, { quadrada: true })}
                  onError={aoErroImagem(foto.url)}
                  alt={`Foto ${i + 1}`}
                  className="img-fluid rounded"
                  style={{ height: 130, width: '100%', objectFit: 'cover', cursor: 'zoom-in' }}
                  loading="lazy"
                  onClick={() => setAmpliada(i)}
                />
                <div className="d-flex justify-content-between align-items-center mt-1">
                  <span className="small text-muted">{i === 0 ? 'Capa' : `#${i + 1}`}</span>
                  {podeEditar && (
                    <span className="btn-group btn-group-sm">
                      <Button variant="outline-secondary" size="sm" disabled={i === 0} onClick={() => mover(i, -1)} aria-label="Mover para trás">
                        ◀
                      </Button>
                      <Button variant="outline-secondary" size="sm" disabled={i === fotos.length - 1} onClick={() => mover(i, 1)} aria-label="Mover para frente">
                        ▶
                      </Button>
                      <Button variant="outline-danger" size="sm" onClick={() => remover(foto)} aria-label="Remover foto">
                        🗑
                      </Button>
                    </span>
                  )}
                </div>
              </div>
            ))}
          </div>
        </>
      )}
      {total >= 2 ? null : (
        <p className="text-warning-emphasis small mt-3">Este imóvel tem menos de 2 fotos — não aparece nos portais.</p>
      )}

      <Modal show={ampliada !== null} onHide={() => setAmpliada(null)} size="lg" centered>
        <Modal.Body className="text-center p-2 bg-dark">
          {ampliada !== null && fotos[ampliada] && (
            <img src={urlOtimizada(fotos[ampliada].url, 1600)} onError={aoErroImagem(fotos[ampliada].url)} alt={`Foto ${ampliada + 1}`} style={{ maxWidth: '100%', maxHeight: '75vh' }} />
          )}
        </Modal.Body>
        <Modal.Footer className="justify-content-between">
          <Button variant="outline-secondary" disabled={ampliada === 0} onClick={() => setAmpliada(ampliada - 1)}>
            ◀ Anterior
          </Button>
          <span className="text-muted">
            {ampliada !== null ? ampliada + 1 : 0} / {fotos.length}
          </span>
          <Button variant="outline-secondary" disabled={ampliada === fotos.length - 1} onClick={() => setAmpliada(ampliada + 1)}>
            Próxima ▶
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={confirmarTodas} onHide={() => setConfirmarTodas(false)} centered>
        <Modal.Header closeButton>
          <Modal.Title className="h5">Remover todas as imagens</Modal.Title>
        </Modal.Header>
        <Modal.Body>Você está removendo todas as imagens deste imóvel. Esta ação não pode ser desfeita.</Modal.Body>
        <Modal.Footer>
          <Button variant="danger" onClick={removerTodas}>
            Sim, remover
          </Button>
          <Button variant="secondary" onClick={() => setConfirmarTodas(false)}>
            Cancelar
          </Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
}
